import React from 'react'
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import { TableContainer, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Table, Paper, Switch, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { X, Trash2 as DeleteIcon, PenBoxIcon, PlusIcon } from 'lucide-react';
import { format, set } from "date-fns";
import CustomButton from '../../componentsII/Buttons';
import AddUpdateSectorModal from '../../components/Masters/SectorMaster/AddUpdateSectorModal';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import { Search } from 'lucide-react';

const SectorMaster = () => {
  const [sector, setSector] = useState([]);
  const [isAddUpdateSectorsModalOpen, setIsAddUpdateSectorsModalOpen] = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectorToDelete, setSectorToDelete] = useState(null);

  const [totalSectors, setTotalSectors] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL',
    sortBy: 'created_date',
    sortOrder: 'desc'
  });

  const getSector = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      const response = await api.get('/admin/getSector', {
        params: {
          page: pageNumber + 1,
          limit,
          search: filterParams.search,
          status: filterParams.status === 'ALL' ? '' : filterParams.status,
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder
        }
      });
      setSector(response.data.data);
      setTotalSectors(response.data.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch users',
        type: 'error',
      }));
    }
  }

  useEffect(() => {
    getSector();
  }, [page, rowsPerPage, filters]);

  const debouncedSearch = debounce((value) => { setFilters(prev => ({ ...prev, search: value })); setPage(0); }, 500);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRemove = async (id) => {
    const sectorDel = sector.find(type => type.id === id);
    setSectorToDelete(sectorDel);
    setDeleteDialogOpen(true);
  }

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      const response = await api.delete(`/admin/deleteSector/${sectorToDelete.id}`);
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: 'Sector deleted successfully',
          type: "success"
        }));
        getSector();
      } else {
        dispatch(showNotification({
          message: 'Failed to delete Sector',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error deleting Sector', error);
      dispatch(showNotification({
        message: 'An error occurred while deleting',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
      setDeleteDialogOpen(false);
      setSectorToDelete(null);
    }
  }

  const ActivateDeactivate = async (user) => {
    try {
      setIsSubmitting(true);
      const response = await api.post(`/admin/activateDeactivateSector/${user.id}`, {
        is_active: user.is_active == "1" ? 0 : 1
      });
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: user.is_active ? 'Sector deactivated successfully' : 'Sector activated successfully',
          type: "success"
        }));
        getSector();
      } else {
        dispatch(showNotification({
          message: user.is_active ? 'Failed to deactivate sector' : 'Failed to activate sector',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error activating/deactivating sector:', error);
      dispatch(showNotification({
        message: 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleEdit = async (user) => {
    setSelectedSector(user);
    setIsAddUpdateSectorsModalOpen(true);
  }

  return (
    <>
      <PageHeader title="Sectors">
        <div className="flex justify-end gap-5 items-center mt-5">
          <div className="flex items-center gap-4 flex-1">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search sectors..."
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => debouncedSearch(e.target.value)}
              className="min-w-[300px]"
            />

            {/* Status Filter */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, status: e.target.value }))
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, sortBy: e.target.value }))
                }}
              >
                <MenuItem value="created_date">Created Date</MenuItem>
                <MenuItem value="sector_name">Sector Name</MenuItem>
              </Select>
            </FormControl>

            {/* Sort Order */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Order</InputLabel>
              <Select
                value={filters.sortOrder}
                label="Order"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, sortOrder: e.target.value }))
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </div>
          <CustomButton
            title='Add Sector'
            className="bg-green-600 hover:bg-green-700 text-white"
            text={<><PlusIcon /> Add Sector</>}
            click={() => setIsAddUpdateSectorsModalOpen(true)}
          />
        </div>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sector Name</TableCell>
                <TableCell>Is Active</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sector?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.sector_name}</TableCell>
                  <TableCell>
                    <Switch
                      checked={user.is_active == "1" ? 1 : user.is_active == "0" ? 0 : null}
                      color='success'
                      onChange={(e) => ActivateDeactivate(user)}
                      disabled={isSubmitting}
                    />
                  </TableCell>
                  <TableCell>
                    {user.created_date ? format(new Date(user.created_date), "dd-MM-yyyy") : ""}
                  </TableCell>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(user)}
                      sx={{
                        backgroundColor: '#16A34A',
                        color: 'white',
                        '&:hover': { backgroundColor: '#00bf46' },
                        p: '8px',
                      }}
                    >
                      <PenBoxIcon size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(user.id)}
                      sx={{
                        backgroundColor: '#df3e3e',
                        color: 'white',
                        '&:hover': { backgroundColor: '#ff0000' },
                        p: '8px',
                      }}
                    >
                      <DeleteIcon size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={7}
                  count={totalSectors}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) => 
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <Dialog
          open={isAddUpdateSectorsModalOpen}
          onClose={() => { setIsAddUpdateSectorsModalOpen(false); setSelectedSector(null) }}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {selectedSector ? "Edit Sector" : "Add New Sector"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => { setIsAddUpdateSectorsModalOpen(false); setSelectedSector(null) }}
            />
          </div>
          <DialogContent >
            <AddUpdateSectorModal
              refetch={getSector}
              onClose={() => {
                setIsAddUpdateSectorsModalOpen(false);
                setSelectedSector(null);
              }}
              sectorId={selectedSector?.id}
              initialData={selectedSector}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the equipment type "{sectorToDelete?.sector_name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Cancel"
                disabled={isSubmitting}
                click={() => setDeleteDialogOpen(false)}
              />
              <CustomButton
                className="bg-red-600 hover:bg-red-700 text-white"
                click={confirmDelete}
                text='Delete'
                disabled={isSubmitting}
              />
            </div>
          </DialogActions>
        </Dialog>
      </PageHeader>
    </>
  )
}

export default SectorMaster